import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useBotProtection } from '../../lib/botProtection'
import './AuthForm.css'

const AuthForm = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const { signInWithMagicLink, isLocalDev, user } = useAuth()
  const navigate = useNavigate()
  
  // Protection anti-bot
  const { userInteraction, createHoneypot, validateSubmission } = useBotProtection()
  const [honeypot, setHoneypot] = useState(null)
  const [honeypotValue, setHoneypotValue] = useState('')

  // Créer le honeypot au montage
  useEffect(() => {
    setHoneypot(createHoneypot())
  }, [createHoneypot])

  // Redirection automatique si déjà connecté
  useEffect(() => {
    if (user) {
      console.log('Utilisateur déjà connecté, redirection vers l\'app...')
      navigate('/app')
    }
  }, [user, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email) {
      setMessage('Veuillez entrer votre adresse email')
      setIsSuccess(false)
      return
    }

    setLoading(true)
    setMessage('')

    try {
      // En mode local, pas besoin de protection anti-bot poussée
      if (!isLocalDev) {
        // Validation anti-bot pour la production
        const formData = {
          [honeypot?.id]: honeypotValue,
          ...userInteraction
        }
        
        const botCheck = await validateSubmission(email, formData)
        
        if (!botCheck.success) {
          setMessage(botCheck.error)
          setIsSuccess(false)
          setLoading(false)
          return
        }
      }

      // Procéder avec l'envoi du Magic Link ou connexion locale
      const { data, error, message } = await signInWithMagicLink(email)

      if (error) {
        setMessage(error)
        setIsSuccess(false)
      } else {
        setMessage(message || 'Connexion en cours...')
        setIsSuccess(true)
        
        // En mode local, rediriger automatiquement après un court délai
        if (isLocalDev && data?.user) {
          setTimeout(() => {
            navigate('/app')
          }, 1000)
        }
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error)
      setMessage('Une erreur s\'est produite. Veuillez réessayer.')
      setIsSuccess(false)
    }

    setLoading(false)
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Application EMDR</h1>
          <p>Connexion sécurisée pour thérapeutes</p>
          {isLocalDev && (
            <div className="dev-mode-badge">
              🔧 Mode Développement Local
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Adresse email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre-email@exemple.com"
              required
              disabled={loading}
            />
          </div>

          {/* Honeypot invisible (seulement en production) */}
          {!isLocalDev && honeypot && (
            <input
              id={honeypot.id}
              type="text"
              value={honeypotValue}
              onChange={(e) => setHoneypotValue(e.target.value)}
              style={honeypot.style}
              tabIndex="-1"
              autoComplete="off"
            />
          )}

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading 
              ? (isLocalDev ? 'Connexion locale...' : 'Vérification en cours...') 
              : (isLocalDev ? 'Se connecter (Local)' : 'Envoyer le lien de connexion')
            }
          </button>
        </form>

        {message && (
          <div className={`auth-message ${isSuccess ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <div className="auth-info">
          <p>
            {isLocalDev ? (
              <>
                🔧 Mode développement local activé<br/>
                📧 Connexion directe sans Magic Link<br/>
                🚀 Redirection automatique vers l'app
              </>
            ) : (
              <>
                🔒 Connexion sans mot de passe via Magic Link<br/>
                📧 Un lien sécurisé sera envoyé à votre email<br/>
                🛡️ Protection anti-spam activée
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

export default AuthForm 